import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  getErrorMessage,
} from 'utils/errors';
import clsx from 'clsx';
import isNil from 'lodash/isNil';
import Spinner from 'pages/Dashboard/components/Spinner';
import useUserInfo from 'pages/Dashboard/hooks/useUserInfo';
import {
  PatientForm,
} from 'pages/Dashboard/types/patient';
import Alert from 'pages/Dashboard/components/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import {
  isEmptyString,
} from '@populateui/utils';
import {
  useERxPage,
} from 'pages/Dashboard/hooks/useERx';

type Props = {
  patient?: PatientForm;
  dosespotMappingId?: number;
};

export default function ERxWebPage({ patient, dosespotMappingId }: Props) {
  const [loaded, setLoaded] = useState<boolean>(false);
  const patientId = patient?.patientId?.toString();
  const userInfo = useUserInfo();
  const eRxEnabled = userInfo?.flags?.haseRxCredentials ?? false;

  const hasRequiredInfo = useMemo(() => (
    isNil(patient?.dosespotError)
  ), [patient]);

  const {
    data: src,
    isError: isDataError,
    error,
    isFetching,
    refetch,
  } = useERxPage({ patientId, dosespotMappingId }, hasRequiredInfo);

  useEffect(() => {
    refetch();
  }, [patient, dosespotMappingId]);

  const hasError = isDataError || !hasRequiredInfo || !eRxEnabled;

  const erxDisabledMessage = !eRxEnabled
    ? 'eRx feature is not enabled for this user account.'
    : null;

  return (
    <>
      {!isEmptyString(src) && !hasError && !isFetching && (
        <iframe
          title="eRx"
          src={src}
          className={clsx(
            'populate-iframe-full bg-transparent relative !w-full !border-0 opacity-0 transition-opacity ease-in duration-100',
            { 'opacity-100': loaded },
          )}
          onLoad={() => { setLoaded(true); }}
        />
      )}
      {(!loaded || isFetching) && !hasError && (
        <Spinner
          className="absolute top-[50%] left-[calc(50%-32px)] translate-[(-50%, -50%)]"
          size={64}
        />
      )}
      {hasError && (
        <div className="text-xl font-semibold p-4">
          {erxDisabledMessage}
          <Alert
            severity="error"
            open={!isNil(patient?.dosespotError) && eRxEnabled}
            icon={false}
          >
            <AlertTitle className="px-4 !m-0 flex w-full items-center justify-between">
              {patient?.dosespotError?.title ?? ''}
            </AlertTitle>
            <p className="px-4 mx-0 mb-0 mt-2 flex w-full items-center justify-between">
              {patient?.dosespotError?.details ?? ''}
            </p>
          </Alert>
          {getErrorMessage(error)
            ?? (hasRequiredInfo && 'Something went wrong. Try to reload this screen.')}
        </div>
      )}
    </>
  );
}
